/** Admin authentication service.
 *
 *  - Login: POST /api/v1/admin/login (username + password)
 *  - Me:    GET  /api/v1/admin/me    (admin JWT)
 *  - Logout: POST /api/v1/admin/logout (admin JWT, jti blacklist)
 *
 *  Token storage: sessionStorage (Sprint 00 only).
 *  ``ADMIN_TOKEN_STORAGE_DEBT = OPEN`` — Sprint 01+ swaps to
 *  HttpOnly Secure SameSite cookie.
 *
 *  The token is intentionally NOT stored in localStorage.
 */
import type {
  AdminLoginRequest,
  AdminLoginResponse,
  AdminLogoutResponse,
  AdminMeResponse,
} from '@/types';
import type { ApiClient } from './api-client';

const TOKEN_STORAGE_KEY = 'mingde.admin.token';
const USER_STORAGE_KEY = 'mingde.admin.user';
const ADMIN_STORAGE_KEY = 'mingde.admin.admin';

export interface StoredPrincipal {
  user: AdminMeResponse['user'];
  admin: AdminMeResponse['admin'];
}

export const authService = {
  loadToken(): string | null {
    try {
      return sessionStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  loadPrincipal(): StoredPrincipal | null {
    try {
      const user = sessionStorage.getItem(USER_STORAGE_KEY);
      const admin = sessionStorage.getItem(ADMIN_STORAGE_KEY);
      if (!user || !admin) return null;
      return {
        user: JSON.parse(user) as AdminMeResponse['user'],
        admin: JSON.parse(admin) as AdminMeResponse['admin'],
      };
    } catch {
      return null;
    }
  },

  saveLoginResponse(payload: AdminLoginResponse): void {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, payload.access_token);
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(payload.user));
    sessionStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(payload.admin));
  },

  clear(): void {
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(USER_STORAGE_KEY);
      sessionStorage.removeItem(ADMIN_STORAGE_KEY);
    } catch {
      /* sessionStorage may be unavailable in SSR / tests */
    }
  },

  async login(
    client: ApiClient,
    payload: AdminLoginRequest,
  ): Promise<AdminLoginResponse> {
    const res = await client.post<AdminLoginResponse>('/api/v1/admin/login', payload);
    authService.saveLoginResponse(res);
    return res;
  },

  async me(client: ApiClient): Promise<AdminMeResponse> {
    return client.get<AdminMeResponse>('/api/v1/admin/me');
  },

  async logout(client: ApiClient): Promise<AdminLogoutResponse> {
    try {
      return await client.post<AdminLogoutResponse>('/api/v1/admin/logout');
    } finally {
      authService.clear();
    }
  },
};